/**
 * Supplier Returns Service
 *
 * API calls for Supplier Returns.
 * @see US-40.3 - Frontend retours fournisseur
 */

import { apiClient } from '@/api/client';
import type {
  SupplierReturn,
  SupplierReturnWithItems,
  CreateSupplierReturnDto,
  UpdateSupplierReturnDto,
  CancelSupplierReturnDto,
  QuerySupplierReturnsDto,
  ApiSuccess,
  SupplierReturnsListResponse,
  SupplierReturnDetailResponse,
  SupplierReturnCreateResponse,
  SupplierReturnUpdateResponse,
  SupplierReturnValidateResponse,
  SupplierReturnCancelResponse,
} from '@bstock/shared';

// Re-export types for convenience
export type {
  SupplierReturn,
  SupplierReturnWithItems,
  CreateSupplierReturnDto,
  UpdateSupplierReturnDto,
  CancelSupplierReturnDto,
  QuerySupplierReturnsDto,
};

export const supplierReturnsService = {
  /**
   * List supplier returns with pagination and filters
   */
  async list(params: QuerySupplierReturnsDto): Promise<SupplierReturnsListResponse> {
    const response = await apiClient.get<ApiSuccess<SupplierReturnsListResponse>>(
      '/supplier-returns',
      { params }
    );
    return response.data.data;
  },

  /**
   * Get single supplier return by ID
   */
  async getById(id: string): Promise<SupplierReturnDetailResponse> {
    const response = await apiClient.get<ApiSuccess<SupplierReturnDetailResponse>>(
      `/supplier-returns/${id}`
    );
    return response.data.data;
  },

  /**
   * Create a new supplier return
   */
  async create(data: CreateSupplierReturnDto): Promise<SupplierReturnCreateResponse> {
    const response = await apiClient.post<ApiSuccess<SupplierReturnCreateResponse>>(
      '/supplier-returns',
      data
    );
    return response.data.data;
  },

  /**
   * Update a supplier return (DRAFT only)
   */
  async update(id: string, data: UpdateSupplierReturnDto): Promise<SupplierReturnUpdateResponse> {
    const response = await apiClient.patch<ApiSuccess<SupplierReturnUpdateResponse>>(
      `/supplier-returns/${id}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete a supplier return (DRAFT only)
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/supplier-returns/${id}`);
  },

  /**
   * Validate a supplier return (DRAFT -> VALIDATED)
   */
  async validate(id: string): Promise<SupplierReturnValidateResponse> {
    const response = await apiClient.post<ApiSuccess<SupplierReturnValidateResponse>>(
      `/supplier-returns/${id}/validate`
    );
    return response.data.data;
  },

  /**
   * Cancel a supplier return (VALIDATED -> CANCELLED)
   */
  async cancel(id: string, reason?: string): Promise<SupplierReturnCancelResponse> {
    const body: CancelSupplierReturnDto = reason ? { reason } : {};
    const response = await apiClient.post<ApiSuccess<SupplierReturnCancelResponse>>(
      `/supplier-returns/${id}/cancel`,
      body
    );
    return response.data.data;
  },
};
